import { getUpNextCalendarEventId } from './upNextEventCalendarCacheSelectors';
import { getTopOfListIndex, shouldShowTasks } from '../selectors/agendaStoreSelectors';
import { agendaListDateWithNoEventsScheduledText } from '../utils/buildListWithDates.locstring.json';
import { isEventInVisualDateRange } from 'owa-calendar-events-visual-date-range';
import { compare, OwaDate } from 'owa-datetime';
import { isToday } from 'owa-observable-datetime';
import type { DateRange } from 'owa-datetime-utils';
import loc from 'owa-localize';
import {
    buildListWithDates,
    getNearestDateAbove,
    isOwaDate,
    isEmptyDateMessage,
    EmptyDateMessage,
} from '../utils/buildListWithDates';
import { assertNever } from 'owa-assert';
import {
    getEvents,
    getLoadedDayDateRanges,
    getLoadedDateRange,
} from './listEventsCalendarCacheSelectors';
import { Todo, isTodoType } from 'owa-todo-types';
import { getTodoCache } from '../store/todoCache';
import { TodoDescriptorType } from 'owa-todo-cache';
import type { AddTaskDateItem } from '../store/schema/AddTaskDateItem';
import { getAgendaItemFromCalendarItem, AgendaItem } from 'owa-agenda-events-common';

export type AgendaItemRow = AgendaItem | OwaDate | EmptyDateMessage | Todo | AddTaskDateItem;

export function getAgendaListItems(scenarioId: string): AgendaItemRow[] {
    let items: AgendaItemRow[] = [];
    const dayDateRanges = getLoadedDayDateRanges(scenarioId);
    // build list
    if (dayDateRanges.length > 0) {
        items = buildListWithDates<AgendaItem>(
            dayDateRanges,
            getItemsForDateRangeFunction(scenarioId),
            loc(agendaListDateWithNoEventsScheduledText)
        );
    }

    // handle empty-state case
    if (!items.some(item => isAgendaItem(item) || isTodoType(item))) {
        return [];
    }

    // handle single-day agenda case
    if (
        items[0] &&
        dayDateRanges[0] &&
        isOwaDate(items[0]) &&
        items[0] === dayDateRanges[0].start &&
        dayDateRanges.length === 1
    ) {
        // remove the first date header, if it is the same as the one showing in the date picker and if we only have one day of data
        items.shift();
    }
    return items;
}

function getItemsForDateRangeFunction(scenarioId: string) {
    return (dateRange: DateRange) => {
        const items = [];
        if (shouldShowTasks(scenarioId)) {
            const todoItems = getTodoCache(scenarioId).getItemsForLock({
                type: TodoDescriptorType.DueDate,
                startDate: dateRange.start,
                endDate: dateRange.end,
            });
            const addTaskItem = isToday(dateRange.start)
                ? { date: dateRange.start, __type: 'AddTaskDateItem' }
                : null;

            if (addTaskItem) {
                items.push(addTaskItem);
            }
            if (todoItems) {
                items.push(...todoItems);
            }
        }
        const eventItems = getEvents(scenarioId, dateRange)
            .filter(isEventInVisualDateRange(dateRange))
            .map(getAgendaItemFromCalendarItem);
        items.push(...eventItems);
        return items;
    };
}

export function getUpNextIndex(scenarioId: string): number {
    const upNextEventId = getUpNextCalendarEventId(scenarioId);
    if (upNextEventId) {
        return getAgendaListItems(scenarioId).map(getId).indexOf(upNextEventId.Id);
    } else {
        return -1;
    }
}

export function getDateHeaderIndex(date: OwaDate, scenarioId: string): number {
    let index = -1;
    const agendaListItems = getAgendaListItems(scenarioId);
    const agendaDates: OwaDate[] = agendaListItems.filter(isOwaDate) as OwaDate[];
    const ids = agendaListItems.map(getId);

    if (compare(date, agendaDates[agendaDates.length - 1]) == 1) {
        // if date is outside the range of given dates
        return -1;
    }

    // assuming agendaDates are sorted, we want the first date that is equal to or
    // after the given date
    for (let i = 0; i < agendaDates.length; i++) {
        let potentialDate = agendaDates[i];
        if (compare(potentialDate, date) >= 0) {
            index = ids.indexOf(getId(potentialDate));
            break;
        }
    }

    return index;
}

/** get the date associated with the items at the top of the list,
 * we pass in the items list to avoid recomputing the list */
export function getListDate(scenarioId: string, items: AgendaItemRow[]): OwaDate {
    const date = getNearestDateAbove(items, getTopOfListIndex(scenarioId));
    /* if there is no date above the item in the list, we return the start of the
    loaded date range, as this is the date associated with the items in the view */
    return date ? date : getLoadedDateRange(scenarioId).start;
}

export function isAgendaItem(item: any): item is AgendaItem {
    return item?.__type === 'AgendaItem';
}

export function isAddTaskDateItem(item: any): item is AddTaskDateItem {
    return item?.__type === 'AddTaskDateItem';
}

function getId(item: AgendaItemRow): string {
    if (isOwaDate(item)) {
        return item.toString();
    } else if (isEmptyDateMessage(item)) {
        return item.text;
    } else if (isTodoType(item)) {
        return item.Id;
    } else if (isAgendaItem(item)) {
        return item.id.Id;
    } else if (isAddTaskDateItem(item)) {
        return `AddTask-${item.date.toString()}`;
    }
    assertNever(item as never);
}
